
// PDP Buy-Gate ARM — early capture-phase tap blocker for APO products.
//
// WHY THIS FILE EXISTS (root cause, verified live 2026-06-28 on the published theme):
//   On MageWorx APO products (the high-AOV configured doors, e.g. hidden-bookcase-door)
//   the OPTION UI renders 100% client-side by the APO bundle. On a throttled phone the
//   native "Add to Cart" button is tappable ~2.5s in, but APO options aren't interactive
//   until ~12s — a ~10s "dead window". A tap in that window submits the BARE base variant
//   with NO Wood Type / Size / Swing options: a misconfigured silent add → bounce, and a
//   real fulfilment/refund landmine. (5 such bare $1,440 "Default Title" adds were found
//   sitting in a live cart.)
//
//   The previous gate (pdp-buy-gate.js, PR #1369) NEVER engaged on these products because
//   (a) its form selector `product-form .product-form` matched nothing — the inner form is
//   `form.form`, the `product-form` class is on the custom element itself — and (b) it
//   detected APO from `.apo-product-options` markers that don't exist in the DOM until APO
//   has already hydrated (too late), while running as a `defer` script that doesn't even
//   execute until ~9s. Net: dead code on the flagship product.
//
// DESIGN:
//   Detection is now SERVER-SIDE: snippets/buy-buttons.liquid sets
//   data-md-apo-gate="true" on <product-form> when product.has_only_default_variant
//   (APO doors delegate ALL configuration to MageWorx → a single "Default Title" variant)
//   AND the product is a door (excludes genuinely-plain single-variant add-ons). This file
//   is loaded by a BLOCKING <script> rendered immediately BEFORE <product-form>, ONLY on
//   gated products — so its document-level capture listeners are armed BEFORE the buy
//   button parses, and no tap can ever slip past. The block decision is made PER TAP
//   (not at load time), which sidesteps every timing race. The gate releases the native
//   button ONLY when APO's validated controls render. It fails CLOSED: if options never
//   load (outage / very slow net) we keep blocking and show a "still loading, refresh /
//   call us" message rather than re-enable the bare, unconfigured add — a misconfigured
//   high-AOV door order is always worse than asking the shopper to retry. 30s ceiling
//   covers Slow-3G APO render (a mobile-3G UAT showed the old 12s window re-opened the leak).
//
//   Visual treatment (loading skeleton + a11y announce + dim) lives in pdp-buy-gate.js,
//   keyed off the same data-md-apo-gate attribute. This file owns ONLY the functional
//   block + the add_to_cart_blocked analytics signal.
(function () {
  'use strict';

  // One instance only, even if buy-buttons.liquid renders more than once (quick-add etc.).
  if (window.__pdpBuyGateArmed) return;
  window.__pdpBuyGateArmed = true;

  // The server-rendered scope marker. Block only taps inside a gated product form.
  var GATE_SELECTOR = 'product-form[data-md-apo-gate="true"]';

  // APO has finished hydrating once its post-init validated controls / option swatches
  // exist. Any one of these proves the real, validated options are interactive.
  var APO_READY_SELECTOR =
    '.mw-apo-buy-it-now-cloned, .mw-apo-add-to-cart-cloned, .mw-apo-container .mw-option';

  // Native purchase controls neutralised during the dead window.
  var NATIVE_BUY_SELECTOR =
    '.product-form__submit, [data-shopify="payment-button"], .shopify-payment-button';

  // Fail-CLOSED ceiling. The gate releases the native buy button ONLY when APO's real
  // validated options render. If they never do (app outage / very slow net), we do NOT
  // re-enable the native button — adding the bare, unconfigured base door is always wrong
  // (a misconfigured high-AOV order). At this ceiling we instead surface a "still loading,
  // refresh / call us" message and keep blocking. 30s comfortably covers APO render on a
  // throttled Slow-3G device, where it routinely exceeds the old 12s window (the gap a
  // mobile 3G UAT surfaced 2026-06-28).
  var FAIL_SAFE_MS = 30000;

  var active = true;
  var lastEmit = 0;

  function apoReady() {
    return !!document.querySelector(APO_READY_SELECTOR);
  }

  // Dual-publish the dead-tap to GTM/GA4 (dataLayer) AND the Super App Web Pixel
  // (Shopify.analytics), mirroring cart-drawer.js _publishCartAddEvent(). A distinct
  // element_action turns the silent leak into a measurable number (frequency + which
  // PDPs), and proves the fix the moment it lands. Debounced so a rapid multi-tap counts
  // once per dead window. No PII — page context only.
  function emitDeadTap() {
    // Staff traffic is never published (snippets/md-internal-traffic.liquid).
    if (window.MD_INTERNAL_TRAFFIC === true) return;
    var now = Date.now();
    if (now - lastEmit < 2000) return;
    lastEmit = now;
    var payload = {
      element_action: 'add_to_cart_blocked',
      element_section: 'pdp_main',
      element_label: 'Add to Cart (options loading)',
      element_position: 'primary_cta',
      page_type: (document.body && document.body.dataset.pageType) || '',
      product_id: (document.body && document.body.dataset.productId) || '',
    };
    try {
      if (window.dataLayer)
        window.dataLayer.push({ event: 'element_click', ...payload });
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
        Shopify.analytics.publish('md_element_click', payload);
      }
    } catch (e) {
      /* silent — analytics must never block UX */
    }
  }

  function blockPrematureBuy(e) {
    if (!active) return;
    var hit = e.target.closest ? e.target.closest(NATIVE_BUY_SELECTOR) : null;
    if (!hit) return;
    // Self-guard + correctness: only block a native buy that belongs to a gated product
    // form whose APO options have NOT yet rendered. Plain products (no gated form) and
    // the post-hydration state both fall straight through.
    if (!document.querySelector(GATE_SELECTOR)) return;
    if (apoReady()) {
      openGate();
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    emitDeadTap();
    // Let the visual layer (pdp-buy-gate.js) nudge the skeleton + ring the tapped button.
    try {
      document.dispatchEvent(
        new CustomEvent('pdp-buy-gate:blocked', {
          bubbles: true,
          detail: { target: hit },
        })
      );
    } catch (err) {
      /* CustomEvent unsupported — block already happened, nudge is non-critical */
    }
  }

  // Capture phase so we intercept BEFORE Shopify's product-form / shop-pay handlers.
  // CLICK ONLY (deliberate): a tap on the native ATC <button> or the express /
  // dynamic-checkout button is click-activated on every browser (the synthesised click
  // from a tap, or Enter for keyboard), so this single capture-phase click block covers
  // native Add-to-Cart AND express buy. We intentionally do NOT register a non-passive
  // `touchstart` listener on `document`: that disables iOS Safari's compositor scroll
  // fast-path for the entire dead window, janking scroll for EVERY user on the PDP — not
  // just tappers. If an express SDK is ever observed starting checkout from a raw touch
  // (not a click) on iOS, scope a non-passive touch listener to that one button, never
  // to `document`.
  document.addEventListener('click', blockPrematureBuy, true);

  // iOS suspends timers + MutationObserver while the tab is backgrounded. If the shopper
  // switches apps and APO finishes hydrating off-screen, resolve the gate the moment they
  // return so it never lingers past its purpose (the fail-open timer's remaining time is
  // frozen during suspension).
  function onVisible() {
    if (!document.hidden && active && apoReady()) openGate();
  }
  document.addEventListener('visibilitychange', onVisible);

  var observer = null;
  var failSafeTimer = null;

  // Release the native button — ONLY called when APO's validated options are actually
  // ready. This is the one and only path that stops the block.
  function openGate() {
    if (!active) return;
    active = false;
    document.removeEventListener('click', blockPrematureBuy, true);
    document.removeEventListener('visibilitychange', onVisible);
    if (observer) observer.disconnect();
    if (failSafeTimer) clearTimeout(failSafeTimer);
    try {
      document.dispatchEvent(new CustomEvent('pdp-buy-gate:open', { bubbles: true }));
    } catch (err) {
      /* non-critical */
    }
  }

  // Fail CLOSED: the ceiling elapsed and APO still hasn't rendered. Keep blocking the
  // bare add (do NOT release), keep the observer alive so a late APO render still
  // releases, and tell the visual layer to show the "still loading / refresh / call"
  // message. We never open the native button into an unconfigured add.
  function onFailSafe() {
    if (apoReady()) {
      openGate();
      return;
    }
    try {
      document.dispatchEvent(
        new CustomEvent('pdp-buy-gate:timeout', { bubbles: true })
      );
    } catch (err) {
      /* non-critical */
    }
  }

  // Tear the gate down the moment APO's validated controls appear.
  function check() {
    if (apoReady()) {
      openGate();
      return true;
    }
    return false;
  }
  if (!check()) {
    observer = new MutationObserver(check);
    // document.body may not be parsed yet (we run before <product-form>); observe
    // documentElement so the subtree watch is valid immediately.
    observer.observe(document.documentElement, { childList: true, subtree: true });
    failSafeTimer = setTimeout(onFailSafe, FAIL_SAFE_MS);
  }

  // Exposed for unit tests / debugging.
  window.__pdpBuyGate = {
    isActive: function () {
      return active;
    },
    open: openGate,
  };
})();
